<template>
  <v-container fluid>
    <PageHeaderSlot showBackButton />

    <v-form ref="dataForm" @submit.prevent="">
      <v-card>
        <v-card-text class="px-2">
          <v-row class="ma-0">
            <v-col cols="12">
              <FormInput
                label="title"
                placeholder="title"
                :fieldValue.sync="formData.title"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
              />
            </v-col>
            <v-col cols="12">
              <FormTextarea
                label="notifyContent"
                :fieldValue.sync="formData.notification_content"
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
              />
            </v-col>
            <v-col cols="12">
              <QuillEditor :html.sync="formData.content" editorID="content" label="content" />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormDatePicker
                label="sendDate"
                placeholder="sendDate"
                :dateValue.sync="formData.send_date_date"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                disabledActionButtons
                disableClear
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormTimePicker
                label="sendTime"
                placeholder="sendTime"
                :fieldValue.sync="formData.send_date_time"
                :options="timeOptions"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormSelect
                label="sendType"
                placeholder="sendType"
                :fieldValue.sync="formData.send_type"
                :options="sendTypeOptions"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                @changed="onSendTypeChanged()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3" v-if="formData.send_type === 'student'">
              <FormSearchSelect
                label="specificStudent"
                :fieldValue.sync="formData.students"
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                multiple
                :options="studentOptions"
                enableClear
                searchPlaceholder="searchStudentData"
                :searching="studentSearching"
                @click-search="searchStudents($event)"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3" v-else-if="formData.send_type === 'lesson'">
              <FormInput
                label="specificLesson"
                placeholder="searchLesson"
                :fieldValue.sync="formData.lesson_input"
                required
                readonly
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                @field-click="openSearchLesson()"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <div class="d-flex align-center mt-4">
        <FormButton @click="save()" />
        <FormButton
          v-if="$validate.DataValid(id)"
          class="ml-4"
          outlined
          color="error"
          text="delete"
          @click="handleDelete()"
        />
      </div>
    </v-form>

    <!-- Modal: 搜尋指定課堂 -->
    <DialogLayout ref="searchLessonDialog" title="searchLesson" hideAction cardContentClass="pa-0">
      <div class="pa-4">
        <v-row class="ma-0 my-4">
          <v-col cols="12" sm="6">
            <FormSelect
              label="searchCourse"
              placeholder="selectCourseName"
              :fieldValue.sync="lessonFilter.keyword"
              :options="courseNameOptions"
              hideDetails
              :readonly="isSearching"
              :isLoading="courseNameSearching"
              @changed="onLessonFilterChange()"
              enableClear
            />
          </v-col>

          <v-col cols="12" sm="6">
            <FormDatePicker
              label="lessonDate"
              :dateValue.sync="lessonFilter.dateRange"
              isDateRange
              @changed="onLessonFilterChange()"
              hideDetails
              placeholder="selectLessonDate"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <FormSelect
              label="tutor"
              :fieldValue.sync="lessonFilter.tutor"
              :options="tutorOptions"
              hideDetails
              :readonly="isSearching"
              placeholder="selectTutor"
              @changed="onLessonFilterChange()"
              enableClear
            />
          </v-col>

          <v-col cols="12" sm="6" v-if="!disableCourseGrade">
            <FormSelect
              label="grade"
              :fieldValue.sync="lessonFilter.grade"
              :options="gradeOptions"
              hideDetails
              :readonly="isSearching"
              placeholder="selectGrade"
              @changed="onLessonFilterChange()"
              enableClear
            />
          </v-col>
        </v-row>
      </div>

      <Datatable
        :isLoading="isSearching"
        :tableData="searchTableData"
        :tableHeaders="lessonTableHeaders"
        :itemTotal="lessonFilter.total"
        :page.sync="lessonFilter.page"
        :pageLimit.sync="lessonFilter.limit"
        @options-update="searchLessons()"
        tableEmptyText="noCourse"
      >
        <template v-slot:[`item.actionSelect`]="{ item }">
          <v-btn
            :loading="$store.getters.isLoading"
            depressed
            text
            color="primary"
            :ripple="false"
            class="pa-0"
            :class="{ 'pointer-none': $store.getters.isLoading }"
            style="min-width: unset !important;"
            @click="handleSelectLesson(item)"
            :disabled="item.disabled"
          >
            {{ $t('select') }}
          </v-btn>
        </template>
      </Datatable>
    </DialogLayout>
  </v-container>
</template>

<script>
import FormInput from '@/components/formField/FormInput.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormTimePicker from '@/components/formField/FormTimePicker.vue'
import QuillEditor from '@/components/formField/QuillEditor.vue'
import FormSearchSelect from '@/components/formField/FormSearchSelect.vue'
import DialogLayout from '@/components/layout/DialogLayout.vue'
import Datatable from '@/components/Datatable.vue'
import FormTextarea from '@/components/formField/FormTextarea.vue'

export default {
  name: 'NotifyDetails',
  inject: ['getConfirmDialogRef'],
  components: {
    FormInput,
    FormSelect,
    FormDatePicker,
    FormTimePicker,
    QuillEditor,
    FormSearchSelect,
    DialogLayout,
    Datatable,
    FormTextarea,
  },
  computed: {
    id() {
      return this.$validate.regexNumber(this.$route.params.id) ? parseInt(this.$route.params.id) : null
    },
    dispatchUpdateOnChange() {
      return this.$validate.DataValid(this.id)
    },
    lessonTableHeaders() {
      let headers = [
        { value: 'course_name', text: this.$t('course') },
        { value: 'full_date', text: this.$t('date'), hasWeekday: true },
        { value: 'timeslot', text: this.$t('time') },
      ]
      if (!this.disableCourseGrade) {
        headers.push({ value: 'grade', text: this.$t('grade') })
      }
      headers = [
        ...headers,
        { value: 'tutor', text: this.$t('tutor') },
        { value: 'actionSelect', text: '', align: 'end' },
      ]
      return headers
    },
    gradeOptions() {
      return this.$store.getters.gradeOptions
    },
  },
  data: vm => ({
    disableCourseGrade: false,
    formData: {
      send_date_date: '',
      send_date_time: '',
      title: '',
      content: '',
      send_type: 'all',
      students: [],
      lesson: -1,
      lesson_input: '',
      notification_content: '',
    },

    studentSearching: false,
    studentOptions: [],

    isSearching: false,
    lessonFilter: {
      keyword: '',
      dateRange: [],
      tutor: null,
      grade: null,
      page: 1,
      total: 0,
      limit: 10,
    },
    courseNameOptions: [],
    courseNameSearching: false,
    searchTableData: [],

    // --- static
    timeOptions: [],
    tutorOptions: [],
    sendTypeOptions: [
      { value: 'all', text: vm.$t('sendTypeOptions.all') },
      { value: 'student', text: vm.$t('sendTypeOptions.student') },
      { value: 'lesson', text: vm.$t('sendTypeOptions.lesson') },
    ],
  }),
  methods: {
    async getNotifyById() {
      try {
        const data = await this.$Fetcher.GetNotifyById(this.id)
        for (const key in this.formData) {
          if (this.$validate.DataValid(data[key])) {
            this.formData[key] = data[key]
          }
        }

        if (data.send_type === 'lesson') {
          await this._getTargetLesson()
        } else if (data.send_type === 'student') {
          await this._getTargetStudents()
        }
      } catch (err) {
        this.$common.error(err)
        if (err !== 'abort') {
          this.showAlert({
            message: 'noData',
            returnLink: 'NotifyList',
          })
        }
      }
    },
    async _getTargetLesson() {
      try {
        const data = await this.$Fetcher.GetLessonById(this.formData.lesson)
        this.formData.lesson_input = `${data.course_name} ${data.date} ${data.start_time} - ${data.end_time}`
      } catch (err) {
        this.$common.error(err)
      }
    },
    async _getTargetStudents() {
      if (this.$validate.DataValid(this.formData.students, true)) {
        this.studentSearching = true
        try {
          const data = await this.$Fetcher.GetStudentsByIdList(this.formData.students)

          this.studentOptions = data.map(item => {
            return { value: item.id, text: `${item.display_student_name} (${item.phone})` }
          })
        } catch {
          this.studentOptions = []
        } finally {
          this.studentSearching = false
        }
      }
    },

    async getSetting() {
      let data = null
      if (this.gEnableCustomGrade) {
        try {
          data = await this.$Fetcher.GetSetting()
        } catch (err) {
          this.$common.error(err)
        }
      }
      this.disableCourseGrade = this.gfCheckDisabledCourseGrade(data)
    },

    // -- (確認)
    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const valid = this.$refs['dataForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', { message: 'required' })
        return
      }

      this.$store.dispatch('setLoading', true)
      try {
        const payload = {
          send_date: `${this.formData.send_date_date}T${this.formData.send_date_time}:00Z`,
          send_type: this.formData.send_type,
          title: this.formData.title,
          content: this.formData.content,
          students: this.formData.send_type === 'student' ? this.formData.students : [],
          lesson: this.formData.send_type === 'lesson' ? this.formData.lesson : -1,
          notification_content: this.formData.notification_content,
        }

        let message = {}
        if (this.$validate.DataValid(this.id)) {
          await this.$Fetcher.UpdateNotify(this.id, payload)
          message = {
            message: 'message.saveSuccess',
            refresh: true,
          }
        } else {
          await this.$Fetcher.NewNotify(payload)
          message = {
            message: 'message.createSuccess',
            redirect: 'NotifyList',
          }
        }

        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('toggleSnack', {
          ...message,
          type: 'success',
        })
      } catch (err) {
        this.$common.error(err)
        this.showAlert({
          title: this.$validate.DataValid(this.id) ? 'message.saveFail' : 'message.createFail',
          message: err,
          isError: true,
        })
      } finally {
        this.$store.dispatch('setLoading', false)
      }
    },

    // -- (刪除)
    async handleDelete() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const confirmRef = this.getConfirmDialogRef()
      const confirm = await confirmRef.show({ isDelete: true })
      if (confirm) {
        this.$store.dispatch('setLoading', true)
        try {
          await this.$Fetcher.DeleteNotify(this.id)
          this.$store.dispatch('toggleSnack', {
            message: 'message.deleteSuccess',
            type: 'success',
            redirect: 'NotifyList',
          })
        } catch (err) {
          this.$common.error(err)
          this.showAlert({
            title: 'message.deleteFail',
            message: err,
            isError: true,
          })
        } finally {
          this.$store.dispatch('setLoading', false)
        }
      }
    },

    onSendTypeChanged() {
      this.formData.students = []
      this.studentOptions = []
      this.studentSearching = false
      this.formData.lesson = -1
      this.formData.lesson_input = ''
    },

    // ============= 指定學生 =============
    async searchStudents(keyword) {
      if (!keyword) {
        this.studentOptions = []
        return
      }

      this.studentSearching = true
      try {
        const { data } = await this.$Fetcher.GetStudents({
          search: keyword,
          filter_item: [{ key: 'status', value: 'active' }],
        })

        this.studentOptions = data.map(item => {
          return { value: item.id, text: `${item.display_student_name} (${item.phone})` }
        })
      } catch {
        this.studentOptions = []
      } finally {
        this.studentSearching = false
      }
    },

    // ============= 指定課堂學生 =============
    async openSearchLesson() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      this.lessonFilter.keyword = ''
      this.lessonFilter.dateRange = []
      this.lessonFilter.tutor = null
      this.lessonFilter.grade = null
      this.lessonFilter.page = 1
      this.lessonFilter.total = 0
      this.searchTableData = []

      this.isSearching = true
      this.$refs.searchLessonDialog.show('search_lesson')
      if (this.tutorOptions.length < 1) {
        await this._getTutorOptions()
      }
      this.searchLessons()

      if (this.courseNameOptions.length < 1 && !this.courseNameSearching) {
        this.courseNameSearching = true
        this.courseNameOptions = await this.$Fetcher.GetAllCourseNames()
        this.courseNameSearching = false
      }
    },
    async _getTutorOptions() {
      try {
        const { data } = await this.$Fetcher.GetStaffs()
        this.tutorOptions = data.map(el => {
          return { value: el.id, text: el.name }
        })
      } catch {}
    },
    async searchLessons() {
      this.isSearching = true
      this.searchTableData = []

      let payload = {
        controller_name: 'search_lesson',
        filter_page: this.lessonFilter.page - 1,
        filter_limit: this.lessonFilter.limit,
        join_tutor_data: true,
        filter_item: [],
      }

      if (this.$validate.DataValid(this.lessonFilter.keyword)) {
        payload['filter_item'].push({ key: 'course_name', value: this.lessonFilter.keyword })
      }

      if (this.$validate.DataValid(this.lessonFilter.tutor) && this.lessonFilter.tutor > -1) {
        payload['filter_item'].push({ key: 'staff_id', value: this.lessonFilter.tutor })
      }

      if (this.$validate.DataValid(this.lessonFilter.grade)) {
        payload['filter_item'].push({ key: 'grade', value: this.lessonFilter.grade })
      }

      if (this.$validate.DataValid(this.lessonFilter.dateRange) && this.lessonFilter.dateRange.length) {
        if (this.lessonFilter.dateRange[0]) {
          if (this.lessonFilter.dateRange.length > 1 && this.lessonFilter.dateRange[1]) {
            payload.filter_item.push({ key: 'start_date', value: this.lessonFilter.dateRange[0] })
            payload.filter_item.push({ key: 'end_date', value: this.lessonFilter.dateRange[1] })
          } else {
            payload['filter_item'].push({ key: 'date', value: this.lessonFilter.dateRange[0] })
          }
        }
      }

      try {
        const { data, total } = await this.$Fetcher.GetLessons(payload)
        this.lessonFilter.total = total
        this.searchTableData = data.map(item => {
          return {
            ...item,
            tutor: item.tutor_data_list.map(t => t.name).join(', '),
          }
        })
      } catch (err) {
        this.$common.error(err)
        if (this.lessonFilter.page === 1) {
          this.lessonFilter.total = 0
        }
        this.searchTableData = []
      } finally {
        this.isSearching = false
      }
    },
    onLessonFilterChange() {
      this.lessonFilter.page = 1
      this.lessonFilter.total = 0
      this.searchLessons()
    },
    // -- (選擇)
    handleSelectLesson(lesson) {
      this.formData.lesson = lesson.id
      this.formData.lesson_input = `${lesson.course_name} ${lesson.date} ${lesson.start_time} - ${lesson.end_time}`
      this.$refs.searchLessonDialog.hide()
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: NotifyDetails')
      await this.getSetting()

      if (this.$validate.DataValid(this.id)) {
        await this.getNotifyById()
      }
      this.$store.dispatch('toggleLoadingPage', false)
    },
  },
  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  created() {
    this.timeOptions = []
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j++) {
        this.timeOptions.push(`${this.$formatter.pad(i)}:${this.$formatter.pad(j)}`)
      }
    }
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else if (this.$store.getters.dataIsUpdated) {
      this.$store.dispatch('showLeaveAlert', { toLink: { name: to.name } })
      next(false)
    } else {
      this.$XHR.abort()
      next()
    }
  },
}
</script>
